import {
  Datagrid,
  IconButton,
  Spacer,
  Spinner,
  Stack,
  Title
} from '@efast_public/fjd-component-library';
import _moment from 'moment';
import React from 'react';
import { useParams } from 'react-router-dom';
import { formatByte } from '../../../../shared/utils/antragsuebersicht';
import styles from './dokumente.module.css';
import BescheidstatusBadge from '../../../shared/BescheidstatusBadge/BescheidstatusBadge';
import { Bescheidstatus } from '../../../../api/models/Status/Bescheidstatus';
import AntragBadge from '../../../shared/AntragBadge/AntragBadge';
import {
  openBlobInNewTab,
  PDF_MIME_TYPE
} from '../../../../shared/utils/fileUtils';
import { useAttachmentDownloadGet } from '../../../../api/hooks/useAttachmentDownload';
import saveAs from 'file-saver';
import { useQuery } from '@tanstack/react-query';
import {
  AttachmentExtendedModel,
  CaseDetailsModel
} from '../../../../api/models/Case/CaseDetailsModel';
import { BehApiRoutes } from '../../../../api/beh-api';
import { AxiosError } from 'axios';

const Antragsdokumente = () => {
  const { caseId } = useParams() as { caseId: string };
  const { downloadAttachment } = useAttachmentDownloadGet();
  const { data: caseDetails } = useQuery<CaseDetailsModel, AxiosError>({
    queryKey: [BehApiRoutes.case(caseId ?? '')]
  });
  const { data: attachments, isLoading } = useQuery<
    Array<AttachmentExtendedModel>,
    AxiosError
  >({
    queryKey: [BehApiRoutes.caseAttachments(caseId ?? '')]
  });
  const initialSubmissionId = caseDetails?.initialSubmission?.submissionId;

  const isBescheidPositivOrNegativ: boolean =
    caseDetails?.bescheidstatus === Bescheidstatus.POSITIV ||
    caseDetails?.bescheidstatus === Bescheidstatus.NEGATIV;

  return (
    <React.Fragment>
      <Title level={2}>Angehängte Dokumente</Title>
      <Datagrid<AttachmentExtendedModel>
        columns={[
          {
            id: 'createdOn',
            header: 'Datum',
            accessorKey: 'createdOn',
            enableHiding: false,
            size: 150,
            minSize: 200,
            enableSorting: true,
            enableResizing: true,
            sortingFn: 'auto',
            cell: (row) => (
              <span>
                {_moment
                  .utc(row.getValue<Date>())
                  .local()
                  .format('DD.MM.YYYY, HH:mm')}
              </span>
            )
          },
          {
            id: 'name',
            header: 'Name',
            accessorKey: 'description',
            enableHiding: false,
            enableSorting: true,
            size: 400,
            minSize: 1000,
            enableResizing: true,
            sortingFn: 'auto',
            cell: (row) => (
              <Stack direction="row" spacing="xl" className={styles.container}>
                <div style={{ whiteSpace: 'initial' }}>
                  {row.row.original.description + row.row.original.filetype}
                </div>
                <Stack
                  direction="row"
                  spacing="xl"
                  className={styles.container}
                >
                  <span
                    className={
                      row.row.original.isVonBehoerde
                        ? styles.file_uploaded_by_authority
                        : styles.file_not_uploaded_by_authority
                    }
                  >
                    {row.row.original.isVonBehoerde
                      ? `von ${row.row.original.absenderName}`
                      : 'von Antragssystem'}
                  </span>
                  {row.row.original.isBescheid &&
                    isBescheidPositivOrNegativ && (
                      <BescheidstatusBadge
                        bescheidstatus={caseDetails?.bescheidstatus!}
                        fjdBadgeSize={'xs'}
                        badgeLabel={'Bescheid'}
                      />
                    )}
                  {row.row.original.submissionId === initialSubmissionId && (
                    <AntragBadge data-testid="antragBadge" />
                  )}
                </Stack>
              </Stack>
            )
          },
          {
            id: 'filetype',
            header: 'Typ',
            accessorKey: 'filetype',
            enableHiding: false,
            size: 100,
            minSize: 200,
            enableSorting: true,
            enableResizing: true,
            sortingFn: 'auto',
            cell: (row) => <span>{row.getValue<string>().toUpperCase()}</span>
          },
          {
            id: 'size',
            header: 'Größe',
            accessorKey: 'size',
            enableHiding: false,
            size: 100,
            minSize: 200,
            enableSorting: true,
            enableResizing: true,
            sortingFn: 'auto',
            cell: (row) => (
              <span>{formatByte(`${row.getValue<string>()}`)}</span>
            )
          },
          {
            id: 'actions',
            header: 'Aktionen',
            accessorKey: 'id',
            enableHiding: false,
            size: 100,
            minSize: 200,
            enableSorting: false,
            enableResizing: false,
            cell: (row) => (
              <Stack direction="row" spacing="xs">
                {row.row.original.filetype === '.pdf' ? (
                  <IconButton
                    variant="primary-text"
                    icon="view"
                    label={'Dokument anzeigen'}
                    size="sm"
                    data-testid={`preview_${row.row.original.id}`}
                    onClick={() => {
                      downloadAttachment(
                        caseId,
                        row.row.original.submissionId,
                        row.row.original.id
                      ).then((file) => {
                        openBlobInNewTab(
                          new File(
                            [file.data],
                            row.row.original.description +
                              row.row.original.filetype,
                            { type: PDF_MIME_TYPE }
                          )
                        );
                      });
                    }}
                  />
                ) : (
                  <Spacer size="s" />
                )}
                <IconButton
                  variant="primary-text"
                  icon="download"
                  label={'Dokument herunterladen'}
                  size="sm"
                  data-testid={`download_${row.row.original.id}`}
                  onClick={() => {
                    downloadAttachment(
                      caseId,
                      row.row.original.submissionId,
                      row.row.original.id
                    ).then((file) => {
                      saveAs(
                        file.data as Blob,
                        row.row.original.description +
                          row.row.original.filetype,
                        { autoBom: false }
                      );
                    });
                  }}
                />
              </Stack>
            )
          }
        ]}
        tableHeight={540}
        data={attachments ?? []}
        dataKey="id"
        isDataLoading={isLoading}
        multiSelect={false}
        areColumnsHideable={false}
        areColumnsResizeable
        manualPagination={false}
        manualSorting={false}
        labels={{
          currentPage: 'Aktuelle Seite',
          detailButton: 'Weitere Infos ein-/ausblenden',
          emptyData: 'Keine Dokumente gefunden.',
          nextPage: 'Nächste Seite',
          of: 'Seite {0} von {1}',
          page: 'Seite',
          pageSize: '{0} Einträge pro Seite',
          previousPage: 'Vorherige Seite',
          rows: 'Zeilen',
          tableTitle: '',
          ascBtn: 'Aufsteigend sortieren',
          descBtn: 'Absteigend sortieren',
          hideBtn: 'Spalte ausblenden'
        }}
      />
    </React.Fragment>
  );
};

export default Antragsdokumente;
